import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import Paginate from '../../components/Paginate';
import {
  useGetCustomersQuery,
  useDeleteCustomerMutation,
  useCreateCustomerMutation,
} from '../../slices/customersApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const CustomerListScreen = () => {
  const { pageNumber } = useParams();
  const { data, isLoading, error, refetch } = useGetCustomersQuery({ pageNumber });
  const [deleteCustomer, { isLoading: loadingDelete }] = useDeleteCustomerMutation();
  const [createCustomer, { isLoading: loadingCreate }] = useCreateCustomerMutation();

  const [filter, setFilter] = useState(''); // Filter text
  const [sortField, setSortField] = useState(''); // Field to sort
  const [sortOrder, setSortOrder] = useState('asc'); // Sort order: 'asc' or 'desc'

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(id).unwrap();
        toast.success('Customer deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const createCustomerHandler = async () => {
    if (window.confirm('Are you sure you want to create a new customer?')) {
      try {
        const newCustomer = {
          name: 'New Customer',
          telephone: '0000000000',
          company: 'Default Company',
          serialNumber: '0000',
          address: 'Default Address',
        };
        await createCustomer(newCustomer).unwrap();
        toast.success('New customer created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const filterAndSortCustomers = () => {
    let filteredCustomers = data?.customers || []; // Ensure data exists

    // Filter customers
    if (filter) {
      filteredCustomers = filteredCustomers.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(filter.toLowerCase()) ||
          customer.company?.toLowerCase().includes(filter.toLowerCase()) ||
          customer.telephone?.includes(filter)
      );
    }

    // Sort customers
    if (sortField) {
      filteredCustomers.sort((a, b) => {
        return sortOrder === 'asc'
          ? a[sortField].localeCompare(b[sortField])
          : b[sortField].localeCompare(a[sortField]);
      });
    }

    return filteredCustomers;
  };

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Customers</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3" onClick={createCustomerHandler}>
            <FaPlus /> Create Customer
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by name, company, or telephone"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error.data?.message}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  NAME {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('company')} style={{ cursor: 'pointer' }}>
                  COMPANY {sortField === 'company' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('telephone')} style={{ cursor: 'pointer' }}>
                  TELEPHONE {sortField === 'telephone' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('serialNumber')} style={{ cursor: 'pointer' }}>
                  SERIAL {sortField === 'serialNumber' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>ADDRESS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortCustomers().map((customer) => (
                <tr key={customer._id}>
                  <td>{customer.name}</td>
                  <td>{customer.company}</td>
                  <td>{customer.telephone}</td>
                  <td>{customer.serialNumber}</td>
                  <td>{customer.address}</td>
                  <td>
                    <Button
                      as={Link}
                      to={`/admin/customer/${customer._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(customer._id)}
                    >
                      <FaTrash style={{ color: 'white' }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data?.pages || 1} page={data?.page || 1} isAdmin={true} type="customerlist" />
        </>
      )}
    </>
  );
};

export default CustomerListScreen;